import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Grid,
} from "@material-ui/core";
import {
  FlashAuto,
  Close,
  ExpandMore,
  ThumbUp,
  ThumbDown,
  Settings,
  Refresh,
} from "@material-ui/icons";
import FileCopyIcon from '@material-ui/icons/FileCopy';
import api from "../../services/api";
import toastError from "../../errors/toastError";
import toastUtils from "../../utils/toastUtils";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingBottom: theme.spacing(2),
  },
  titleIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  closeButton: {
    color: theme.palette.grey[500],
  },
  suggestionCard: {
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      boxShadow: theme.shadows[4],
    },
  },
  suggestionContent: {
    paddingBottom: 0,
  },
  confidenceChip: {
    fontWeight: 'bold',
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(4),
  },
  configSection: {
    marginBottom: theme.spacing(2),
  },
  formRow: {
    marginBottom: theme.spacing(2),
  },
  sliderContainer: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  templateChip: {
    margin: theme.spacing(0.5),
    cursor: 'pointer',
  },
  selectedTemplate: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
}));

const CampaignAiSuggestionsModal = ({
  open,
  onClose,
  onSelectSuggestion,
  campaignData = null
}) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showConfig, setShowConfig] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [models, setModels] = useState([]);

  // Configurações para geração de sugestões
  const [config, setConfig] = useState({
    campaignName: campaignData?.name || '',
    baseMessage: campaignData?.message1 || '',
    productService: '',
    targetAudience: '',
    campaignGoal: '',
    tone: 'professional',
    additionalContext: '',
    campaignType: 'promotional',
    messageStyle: 'persuasive',
    temperature: 0.7,
    maxTokens: 1200,
    maxSuggestions: 5,
    language: 'pt',
    model: 'gpt-4o',
  });

  useEffect(() => {
    if (open) {
      loadTemplates();
      loadModels();
    }
  }, [open]);

  const loadTemplates = async () => {
    try {
      const { data } = await api.get("/campaign-ai-suggestions/templates");
      setTemplates(data.templates || []);
    } catch (err) {
      console.error("Erro ao carregar templates:", err);
    }
  };

  const loadModels = async () => {
    try {
      const { data } = await api.get("/campaign-ai-suggestions/models");
      setModels(data.availableModels || []);
      if (data.currentModel) {
        setConfig(prev => ({ ...prev, model: data.currentModel }));
      }
    } catch (err) {
      console.error("Erro ao carregar modelos:", err);
    }
  };

  const handleGenerateSuggestions = async () => {
    // Validar campos obrigatórios
    if (!config.campaignName || !config.productService || !config.targetAudience || !config.campaignGoal) {
      toastUtils.error(
        "Preencha todos os campos obrigatórios: Nome da campanha, Produto/Serviço, Público-alvo e Objetivo da campanha",
        "Campos Obrigatórios"
      );
      return;
    }

    setLoading(true);
    try {
      const payload = {
        campaignName: config.campaignName,
        baseMessage: config.baseMessage,
        promptInputs: {
          productService: config.productService,
          targetAudience: config.targetAudience,
          campaignGoal: config.campaignGoal,
          tone: config.tone,
          additionalContext: config.additionalContext,
        },
        localConfig: {
          temperature: config.temperature,
          maxTokens: config.maxTokens,
          confidenceThreshold: 0.7,
          campaignType: config.campaignType,
          messageStyle: config.messageStyle,
        },
        model: config.model,
        language: config.language,
        maxSuggestions: config.maxSuggestions,
      };

      const { data } = await api.post("/campaign-ai-suggestions/generate", payload);
      setSuggestions(data.suggestions || []);

      toastUtils.success(
        `${data.suggestions?.length || 0} sugestões geradas com sucesso!`,
        "Sugestões de IA"
      );
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUseSuggestion = (suggestion) => {
    if (onSelectSuggestion) {
      onSelectSuggestion(suggestion.content);
    }

    // Registrar feedback positivo automaticamente
    recordFeedback(suggestion.id, true, true);

    toastUtils.success(
      "Sugestão aplicada com sucesso!",
      "Campanha IA"
    );
  };

  const handleCopySuggestion = (suggestion) => {
    navigator.clipboard.writeText(suggestion.content);
    toastUtils.success(
      "Sugestão copiada para área de transferência!",
      "Copiado"
    );
  };

  const recordFeedback = async (suggestionId, helpful, used = false) => {
    try {
      await api.post(`/campaign-ai-suggestions/suggestions/${suggestionId}/feedback`, {
        helpful,
        used,
        campaignName: config.campaignName,
      });
    } catch (err) {
      console.error("Erro ao registrar feedback:", err);
    }
  };

  const applyTemplate = (template) => {
    setConfig(prev => ({
      ...prev,
      campaignType: template.defaultConfig.campaignType,
      messageStyle: template.defaultConfig.messageStyle,
      temperature: template.defaultConfig.temperature,
      maxTokens: template.defaultConfig.maxTokens,
      tone: template.promptSuggestions.tone,
      campaignGoal: template.promptSuggestions.campaignGoal,
    }));

    toastUtils.success(`Template "${template.name}" aplicado!`, "Template");
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return "primary";
    if (confidence >= 0.6) return "default";
    return "secondary";
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Box display="flex" alignItems="center">
          <FlashAuto className={classes.titleIcon} />
          <Typography variant="h6">
            {i18n.t("aiSuggestions.campaignTitle")}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center">
          <Tooltip title="Configurações Avançadas">
            <IconButton onClick={() => setShowConfig(!showConfig)}>
              <Settings />
            </IconButton>
          </Tooltip>
          <IconButton
            onClick={onClose}
            className={classes.closeButton}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Seção de Configurações */}
        <Accordion expanded={showConfig} onChange={() => setShowConfig(!showConfig)}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6">
              {i18n.t("aiSuggestions.configuration.title")}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box width="100%">
              {/* Templates */}
              <Box className={classes.configSection}>
                <Typography variant="subtitle2" gutterBottom>
                  Templates de Campanha:
                </Typography>
                <Box display="flex" flexWrap="wrap">
                  {templates.map((template) => (
                    <Chip
                      key={template.id}
                      label={template.name}
                      className={`${classes.templateChip} ${
                        config.campaignType === template.id ? classes.selectedTemplate : ''
                      }`}
                      onClick={() => applyTemplate(template)}
                      variant={config.campaignType === template.id ? "default" : "outlined"}
                    />
                  ))}
                </Box>
              </Box>

              {/* Campos obrigatórios */}
              <Grid container spacing={2} className={classes.formRow}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={i18n.t("aiSuggestions.campaign.nameLabel")}
                    value={config.campaignName}
                    onChange={(e) => setConfig(prev => ({ ...prev, campaignName: e.target.value }))}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={i18n.t("aiSuggestions.campaign.baseMessageLabel")}
                    value={config.baseMessage}
                    onChange={(e) => setConfig(prev => ({ ...prev, baseMessage: e.target.value }))}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} className={classes.formRow}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={i18n.t("aiSuggestions.campaign.productServiceLabel")}
                    value={config.productService}
                    onChange={(e) => setConfig(prev => ({ ...prev, productService: e.target.value }))}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={i18n.t("aiSuggestions.campaign.targetAudienceLabel")}
                    value={config.targetAudience}
                    onChange={(e) => setConfig(prev => ({ ...prev, targetAudience: e.target.value }))}
                    required
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} className={classes.formRow}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={i18n.t("aiSuggestions.campaign.campaignGoalLabel")}
                    value={config.campaignGoal}
                    onChange={(e) => setConfig(prev => ({ ...prev, campaignGoal: e.target.value }))}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>{i18n.t("aiSuggestions.campaign.toneLabel")}</InputLabel>
                    <Select
                      value={config.tone}
                      onChange={(e) => setConfig(prev => ({ ...prev, tone: e.target.value }))}
                    >
                      <MenuItem value="professional">Profissional</MenuItem>
                      <MenuItem value="casual">Casual</MenuItem>
                      <MenuItem value="promotional">Promocional</MenuItem>
                      <MenuItem value="empathetic">Empático</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* Configurações avançadas */}
              <Grid container spacing={2} className={classes.formRow}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>{i18n.t("aiSuggestions.campaign.modelLabel")}</InputLabel>
                    <Select
                      value={config.model}
                      onChange={(e) => setConfig(prev => ({ ...prev, model: e.target.value }))}
                    >
                      {models.map((model) => (
                        <MenuItem key={model.id} value={model.id}>
                          {model.name} {model.recommended && "(Recomendado)"}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography gutterBottom>
                    {i18n.t("aiSuggestions.campaign.temperatureLabel")}: {config.temperature}
                  </Typography>
                  <div className={classes.sliderContainer}>
                    <Slider
                      value={config.temperature}
                      onChange={(e, value) => setConfig(prev => ({ ...prev, temperature: value }))}
                      min={0.1}
                      max={1.0}
                      step={0.1}
                      valueLabelDisplay="auto"
                    />
                  </div>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography gutterBottom>
                    {i18n.t("aiSuggestions.campaign.maxSuggestionsLabel")}: {config.maxSuggestions}
                  </Typography>
                  <div className={classes.sliderContainer}>
                    <Slider
                      value={config.maxSuggestions}
                      onChange={(e, value) => setConfig(prev => ({ ...prev, maxSuggestions: value }))}
                      min={1}
                      max={10}
                      step={1}
                      valueLabelDisplay="auto"
                    />
                  </div>
                </Grid>
              </Grid>

              <TextField
                fullWidth
                multiline
                rows={3}
                label={i18n.t("aiSuggestions.campaign.additionalContextLabel")}
                value={config.additionalContext}
                onChange={(e) => setConfig(prev => ({ ...prev, additionalContext: e.target.value }))}
                className={classes.formRow}
              />
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Botão de Geração */}
        <Box textAlign="center" mt={2} mb={2}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={loading ? <CircularProgress size={20} /> : <FlashAuto />}
            onClick={handleGenerateSuggestions}
            disabled={loading}
          >
            {loading ? i18n.t("aiSuggestions.messages.generating") : i18n.t("aiSuggestions.buttons.generate")}
          </Button>
        </Box>

        {/* Lista de Sugestões */}
        {loading && (
          <Box className={classes.loadingContainer}>
            <CircularProgress size={40} />
            <Typography variant="body2" style={{ marginTop: 16 }}>
              {i18n.t("aiSuggestions.messages.generating")}
            </Typography>
          </Box>
        )}

        {!loading && suggestions.length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              {suggestions.length} {i18n.t("aiSuggestions.campaign.nameLabel")} Geradas:
            </Typography>
            {suggestions.map((suggestion, index) => (
              <Card key={index} className={classes.suggestionCard}>
                <CardContent className={classes.suggestionContent}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Typography variant="subtitle2" color="primary">
                      Sugestão #{index + 1}
                    </Typography>
                    <Chip
                      label={`${Math.round((suggestion.confidence || 0.7) * 100)}%`}
                      size="small"
                      color={getConfidenceColor(suggestion.confidence || 0.7)}
                      className={classes.confidenceChip}
                    />
                  </Box>
                  <Typography variant="body1" paragraph>
                    {suggestion.content}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    color="primary"
                    startIcon={<FileCopyIcon />}
                    onClick={() => handleCopySuggestion(suggestion)}
                  >
                    {i18n.t("aiSuggestions.buttons.copy")}
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => handleUseSuggestion(suggestion)}
                  >
                    {i18n.t("aiSuggestions.buttons.use")}
                  </Button>
                  <Box flexGrow={1} />
                  <Tooltip title="Útil">
                    <IconButton
                      size="small"
                      onClick={() => recordFeedback(suggestion.id, true)}
                    >
                      <ThumbUp fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Não útil">
                    <IconButton
                      size="small"
                      onClick={() => recordFeedback(suggestion.id, false)}
                    >
                      <ThumbDown fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            ))}
          </Box>
        )}

        {!loading && suggestions.length === 0 && config.campaignName && (
          <Box textAlign="center" p={4}>
            <Typography variant="body2" color="textSecondary">
              {i18n.t("aiSuggestions.messages.noSuggestions")}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="default">
          {i18n.t("aiSuggestions.buttons.close")}
        </Button>
        <Button
          onClick={handleGenerateSuggestions}
          color="primary"
          startIcon={<Refresh />}
          disabled={loading}
        >
          {i18n.t("aiSuggestions.buttons.refresh")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CampaignAiSuggestionsModal;